import type { PathOptions } from "leaflet";
import type { StatusValue } from "../types";
import type { EffectiveStatusValue } from "./apiTypes";
import { EFFECTIVE_STATUS_VALUES } from "./apiTypes";
import { computeGlance, GLANCE_METADATA, type Glance, type GlanceSource, type GlanceTier } from "./glance";

export interface StatusMetadata {
  value: EffectiveStatusValue;
  label: string;
  color: string;
  cue: string;
}

/**
 * Raw status vocabulary (SPEC §6). Since UX round 3 the map is painted by glance tier
 * (`GLANCE_METADATA`); these labels survive as the secondary "what VECO said" pill.
 */
export const STATUS_METADATA: Record<EffectiveStatusValue, StatusMetadata> = {
  none: { value: "none", label: "No outage scheduled", color: "#737373", cue: "solid" },
  possible: { value: "possible", label: "Possible rotational outage", color: "#E69F00", cue: "dotted outline" },
  scheduled: { value: "scheduled", label: "Scheduled outage", color: "#0072B2", cue: "solid outline" },
  ongoing: { value: "ongoing", label: "Outage ongoing", color: "#D55E00", cue: "thick outline" },
  restored: { value: "restored", label: "Restored today", color: "#009E73", cue: "solid outline" },
  cancelled: { value: "cancelled", label: "Cancelled", color: "#6F4E7C", cue: "dashed outline" },
  unknown: { value: "unknown", label: "Unknown", color: "#CC79A7", cue: "fine dotted outline" },
};

export function normalizeStatus(status: StatusValue | EffectiveStatusValue | undefined): EffectiveStatusValue {
  if (status && EFFECTIVE_STATUS_VALUES.includes(status as EffectiveStatusValue)) {
    return status as EffectiveStatusValue;
  }
  return "unknown";
}

interface TierPaint {
  fillOpacity: number;
  weight: number;
  dashArray?: string;
}

/** R-608: loud tiers stay loud, quiet tiers recede; the outline cue is independent of colour. */
const TIER_PAINT: Record<GlanceTier, TierPaint> = {
  off: { fillOpacity: 0.62, weight: 3 },
  soon: { fillOpacity: 0.58, weight: 2 },
  later: { fillOpacity: 0.42, weight: 2, dashArray: "8 5" },
  clear: { fillOpacity: 0.16, weight: 1 },
  unknown: { fillOpacity: 0.32, weight: 1, dashArray: "2 4" },
};

export function tierStyle(tier: GlanceTier, stale: boolean, possible = false): PathOptions {
  const metadata = GLANCE_METADATA[tier];
  const paint = TIER_PAINT[tier];
  return {
    color: metadata.stroke,
    fillColor: metadata.color,
    fillOpacity: paint.fillOpacity,
    opacity: 1,
    weight: paint.weight,
    dashArray: possible && !paint.dashArray ? "3 5" : paint.dashArray,
    className: `status-polygon tier-${tier}${possible ? " is-possible" : ""}${stale ? " is-stale" : ""}`,
  };
}

export function glanceStyle(glance: Glance, stale: boolean): PathOptions {
  return tierStyle(glance.tier, stale, glance.possible);
}

export function selectedGlanceStyle(glance: Glance, stale: boolean): PathOptions {
  const base = glanceStyle(glance, stale);
  return {
    ...base,
    color: "#0B0F14",
    weight: 5,
    opacity: 1,
    dashArray: undefined,
    className: `${String(base.className ?? "")} is-selected`,
  };
}

/** Convenience for callers that only have a status row: computes the glance first. */
export function statusStyle(source: GlanceSource, stale: boolean, now: Date = new Date()): PathOptions {
  return glanceStyle(computeGlance(source, now), stale);
}

export function selectedStatusStyle(source: GlanceSource, stale: boolean, now: Date = new Date()): PathOptions {
  return selectedGlanceStyle(computeGlance(source, now), stale);
}
